import naja from 'naja';
import CustomSelect from './CustomSelect';

interface OrderCartFormElements {
    formNewsletter: HTMLInputElement | null;
    formTerms: HTMLInputElement | null;
    formSubmit: HTMLInputElement | null;
    summaryNewsletter: HTMLInputElement | null;
    summaryTerms: HTMLInputElement | null;
    summarySubmit: HTMLButtonElement | null;
    countrySelect: HTMLInputElement | null;
    countrySelectContainer: HTMLElement | null;
    opportunitySelect: HTMLInputElement | null;
    opportunitySelectContainer: HTMLElement | null;
    phonePrecodeInput: HTMLInputElement | null;
    phonePrecodeSelect: HTMLElement | null;
    phonePrecodeTextInput: HTMLInputElement | null;
    streetInput: HTMLInputElement | null;
    cityInput: HTMLInputElement | null;
    zipInput: HTMLInputElement | null;
    deliveryDateInput: HTMLInputElement | null;
    noteTextarea: HTMLTextAreaElement | null;
    emailInput: HTMLInputElement | null;
    passwordInput: HTMLInputElement | null;
    loginButton: HTMLButtonElement | null;
    loginMessage: HTMLElement | null;
    forgotPasswordLink: HTMLElement | null;
    continueWithoutLoginLink: HTMLElement | null;
    forgotPasswordContainer: HTMLElement | null;
    sendResetButton: HTMLButtonElement | null;
}

export default class OrderCartFormSync {
    private elements: OrderCartFormElements;
    private checkboxStates: { newsletter: boolean; terms: boolean } = { newsletter: false, terms: false };
    private najaHandlersSetup = false;
    private isSubmitting = false;
    private boundHandlers: {
        summarySubmitHandler?: () => void;
        formNewsletterHandler?: () => void;
        summaryNewsletterHandler?: () => void;
        formTermsHandler?: () => void;
        summaryTermsHandler?: () => void;
        phonePrecodeHandler?: (e: Event) => void;
        emailBlurHandler?: () => void;
        loginButtonHandler?: () => void;
        forgotPasswordHandler?: (e: Event) => void;
        continueWithoutLoginHandler?: (e: Event) => void;
        sendResetHandler?: () => void;
    } = {};

    private readonly countryToPrefixMap: Record<string, string> = {
        cz: '+420',
        sk: '+421',
        pl: '+48',
        de: '+49',
        at: '+43',
        be: '+32',
        bg: '+359',
        dk: '+45',
        ee: '+372',
        ie: '+353',
        gr: '+30',
        es: '+34',
        fr: '+33',
        hr: '+385',
        it: '+39',
        cy: '+357',
        lv: '+371',
        lt: '+370',
        lu: '+352',
        hu: '+36',
        mt: '+356',
        nl: '+31',
        pt: '+351',
        ro: '+40',
        si: '+386',
        fi: '+358',
        se: '+46',
        ua: '+380',
    };

    constructor() {
        this.elements = this.getElements();
        this.init();
    }

    private getElements(): OrderCartFormElements {
        const countrySelect = document.querySelector('input[name="country"]') as HTMLInputElement | null;
        const opportunitySelect = document.querySelector('input[name="opportunity"]') as HTMLInputElement | null;

        return {
            formNewsletter: document.getElementById('form-newsletter') as HTMLInputElement | null,
            formTerms: document.getElementById('form-terms') as HTMLInputElement | null,
            formSubmit: document.getElementById('form-submit') as HTMLInputElement | null,
            summaryNewsletter: document.getElementById('summary-newsletter') as HTMLInputElement | null,
            summaryTerms: document.getElementById('summary-terms') as HTMLInputElement | null,
            summarySubmit: document.getElementById('summary-submit') as HTMLButtonElement | null,
            countrySelect: countrySelect,
            countrySelectContainer: countrySelect?.parentElement?.querySelector(
                '.select-container-simple',
            ) as HTMLElement | null,
            opportunitySelect: opportunitySelect,
            opportunitySelectContainer: opportunitySelect?.parentElement?.querySelector(
                '.select-container-simple',
            ) as HTMLElement | null,
            phonePrecodeInput: document.getElementById('phone-precode-input') as HTMLInputElement | null,
            phonePrecodeSelect: document.getElementById('phone-precode-select') as HTMLElement | null,
            phonePrecodeTextInput: document.getElementById('phone-precode-text-input') as HTMLInputElement | null,
            streetInput: document.querySelector('input[name="street"]') as HTMLInputElement | null,
            cityInput: document.querySelector('input[name="city"]') as HTMLInputElement | null,
            zipInput: document.querySelector('input[name="zip"]') as HTMLInputElement | null,
            deliveryDateInput: document.querySelector('input[name="delivery_date"]') as HTMLInputElement | null,
            noteTextarea: document.querySelector('textarea[name="note"]') as HTMLTextAreaElement | null,
            emailInput: document.querySelector('input[name="email"]') as HTMLInputElement | null,
            passwordInput: document.querySelector('input[name="password"]') as HTMLInputElement | null,
            loginButton: document.getElementById('ajax-login-btn') as HTMLButtonElement | null,
            loginMessage: document.getElementById('login-message') as HTMLElement | null,
            forgotPasswordLink: document.getElementById('forgot-password-link') as HTMLElement | null,
            continueWithoutLoginLink: document.getElementById('continue-without-login-link') as HTMLElement | null,
            forgotPasswordContainer: document.getElementById('forgot-password-container') as HTMLElement | null,
            sendResetButton: document.getElementById('send-reset-btn') as HTMLButtonElement | null,
        };
    }

    private init(): void {
        this.cleanup();
        this.syncCheckboxes();
        this.syncInitialFormState();
        this.setupSubmitHandler();
        this.setupPhonePrecodeSync();
        this.setupCustomSelectHandlers();
        this.setupEmailValidation();
        this.setupLoginButton();
        this.setupForgotPasswordLink();
        this.setupContinueWithoutLoginLink();
        this.setupSendResetButton();
        this.setupNajaSnippetHandler();
        this.setupSubmissionStateHandlers();
    }

    private cleanup(): void {
        // Remove existing event listeners to prevent duplicates
        if (this.elements.summarySubmit && this.boundHandlers.summarySubmitHandler) {
            this.elements.summarySubmit.removeEventListener('click', this.boundHandlers.summarySubmitHandler);
        }
        if (this.elements.formNewsletter && this.boundHandlers.formNewsletterHandler) {
            this.elements.formNewsletter.removeEventListener('change', this.boundHandlers.formNewsletterHandler);
        }
        if (this.elements.summaryNewsletter && this.boundHandlers.summaryNewsletterHandler) {
            this.elements.summaryNewsletter.removeEventListener('change', this.boundHandlers.summaryNewsletterHandler);
        }
        if (this.elements.formTerms && this.boundHandlers.formTermsHandler) {
            this.elements.formTerms.removeEventListener('change', this.boundHandlers.formTermsHandler);
        }
        if (this.elements.summaryTerms && this.boundHandlers.summaryTermsHandler) {
            this.elements.summaryTerms.removeEventListener('change', this.boundHandlers.summaryTermsHandler);
        }
        if (this.elements.countrySelect && this.boundHandlers.phonePrecodeHandler) {
            const countrySelectContainer =
                this.elements.countrySelect.parentElement?.querySelector('.select-container-simple');
            if (countrySelectContainer) {
                countrySelectContainer.removeEventListener('selectChange', this.boundHandlers.phonePrecodeHandler);
            }
        }
        if (this.elements.emailInput && this.boundHandlers.emailBlurHandler) {
            this.elements.emailInput.removeEventListener('blur', this.boundHandlers.emailBlurHandler);
        }
        if (this.elements.loginButton && this.boundHandlers.loginButtonHandler) {
            this.elements.loginButton.removeEventListener('click', this.boundHandlers.loginButtonHandler);
        }
        if (this.elements.forgotPasswordLink && this.boundHandlers.forgotPasswordHandler) {
            this.elements.forgotPasswordLink.removeEventListener('click', this.boundHandlers.forgotPasswordHandler);
        }
        if (this.elements.continueWithoutLoginLink && this.boundHandlers.continueWithoutLoginHandler) {
            this.elements.continueWithoutLoginLink.removeEventListener(
                'click',
                this.boundHandlers.continueWithoutLoginHandler,
            );
        }
        if (this.elements.sendResetButton && this.boundHandlers.sendResetHandler) {
            this.elements.sendResetButton.removeEventListener('click', this.boundHandlers.sendResetHandler);
        }

        // Clear stored handlers
        this.boundHandlers = {};
    }

    private syncCheckboxes(): void {
        // Sync newsletter checkbox
        if (this.elements.summaryNewsletter && this.elements.formNewsletter) {
            this.boundHandlers.summaryNewsletterHandler = () => {
                if (this.elements.formNewsletter) {
                    this.elements.formNewsletter.checked = this.elements.summaryNewsletter!.checked;
                }
            };

            this.boundHandlers.formNewsletterHandler = () => {
                if (this.elements.summaryNewsletter) {
                    this.elements.summaryNewsletter.checked = this.elements.formNewsletter!.checked;
                }
            };

            this.elements.summaryNewsletter.addEventListener('change', this.boundHandlers.summaryNewsletterHandler);
            this.elements.formNewsletter.addEventListener('change', this.boundHandlers.formNewsletterHandler);
        }

        // Sync terms checkbox
        if (this.elements.summaryTerms && this.elements.formTerms) {
            this.boundHandlers.summaryTermsHandler = () => {
                if (this.elements.formTerms) {
                    this.elements.formTerms.checked = this.elements.summaryTerms!.checked;
                }
            };

            this.boundHandlers.formTermsHandler = () => {
                if (this.elements.summaryTerms) {
                    this.elements.summaryTerms.checked = this.elements.formTerms!.checked;
                }
            };

            this.elements.summaryTerms.addEventListener('change', this.boundHandlers.summaryTermsHandler);
            this.elements.formTerms.addEventListener('change', this.boundHandlers.formTermsHandler);
        }
    }

    private syncInitialFormState(): void {
        // Sync initial checkbox states from form to summary when page loads
        if (this.elements.formNewsletter && this.elements.summaryNewsletter) {
            this.elements.summaryNewsletter.checked = this.elements.formNewsletter.checked;
        }

        if (this.elements.formTerms && this.elements.summaryTerms) {
            this.elements.summaryTerms.checked = this.elements.formTerms.checked;
        }
    }

    private setupSubmitHandler(): void {
        if (!this.elements.summarySubmit || !this.elements.formSubmit) {
            return;
        }

        this.boundHandlers.summarySubmitHandler = () => {
            // Prevent duplicate submissions
            if (this.isSubmitting) {
                return;
            }

            // Validate the terms checkbox first
            if (!this.elements.formTerms?.checked) {
                alert('Musíte souhlasit s obchodními podmínkami.');
                return;
            }

            // Check if password is required but not filled
            if (
                this.elements.passwordInput &&
                this.elements.passwordInput.hasAttribute('required') &&
                !this.elements.passwordInput.value
            ) {
                alert('Tento e-mail je již registrován. Prosím zadejte heslo pro přihlášení.');
                this.elements.passwordInput.focus();
                return;
            }

            // Get the form element
            const form = this.elements.formSubmit?.closest('form') as HTMLFormElement;
            if (form) {
                // Validate the form first
                if (form.checkValidity()) {
                    this.isSubmitting = true;
                    this.elements.formSubmit?.click();
                } else {
                    // Show validation errors
                    form.reportValidity();
                }
            }
        };

        this.elements.summarySubmit.addEventListener('click', this.boundHandlers.summarySubmitHandler);
    }

    private setupPhonePrecodeSync(): void {
        if (!this.elements.countrySelect) {
            return;
        }

        // Listen for country selection changes via CustomSelect event
        // The hidden input and select container are siblings, not parent-child
        const countrySelectContainer =
            this.elements.countrySelect.parentElement?.querySelector('.select-container-simple');

        if (countrySelectContainer) {
            this.boundHandlers.phonePrecodeHandler = (e: Event) => {
                const customEvent = e as CustomEvent<{
                    value: string;
                    text: string;
                    option: HTMLElement;
                }>;
                const countryCode = customEvent.detail.value;

                if (countryCode === 'non-eu') {
                    // Switch to text input for non-EU countries
                    this.switchToTextInput();
                } else if (countryCode && this.countryToPrefixMap[countryCode]) {
                    // Switch to dropdown and update prefix for EU countries
                    this.switchToDropdown();
                    const newPrefix = this.countryToPrefixMap[countryCode];
                    this.updatePhonePrecode(newPrefix);
                } else {
                    // Switch to dropdown for unknown countries (fallback)
                    this.switchToDropdown();
                }
            };

            countrySelectContainer.addEventListener('selectChange', this.boundHandlers.phonePrecodeHandler);
        }
    }

    private setupCustomSelectHandlers(): void {
        // Setup country select handler (updates hidden input)
        if (this.elements.countrySelectContainer && this.elements.countrySelect) {
            const countryHandler = (e: Event) => {
                const customEvent = e as CustomEvent<{
                    value: string;
                    text: string;
                    option: HTMLElement;
                }>;
                if (this.elements.countrySelect) {
                    this.elements.countrySelect.value = customEvent.detail.value;
                }
            };

            this.elements.countrySelectContainer.addEventListener('selectChange', countryHandler);
        }

        // Setup opportunity select handler (updates hidden input)
        if (this.elements.opportunitySelectContainer && this.elements.opportunitySelect) {
            const opportunityHandler = (e: Event) => {
                const customEvent = e as CustomEvent<{
                    value: string;
                    text: string;
                    option: HTMLElement;
                }>;
                if (this.elements.opportunitySelect) {
                    this.elements.opportunitySelect.value = customEvent.detail.value;
                }
            };

            this.elements.opportunitySelectContainer.addEventListener('selectChange', opportunityHandler);
        }
    }

    private setupEmailValidation(): void {
        if (!this.elements.emailInput || !this.elements.passwordInput) {
            return;
        }

        this.boundHandlers.emailBlurHandler = () => {
            const email = this.elements.emailInput?.value;
            if (!email || email.trim() === '') {
                return;
            }

            // Check if user is already logged in by looking for user menu or other indicator
            const userMenu = document.querySelector('.user-menu--logged-in');
            const loggedInIndicator = document.querySelector('[data-user-logged-in]');
            if (userMenu || loggedInIndicator) {
                // User is already logged in, no need to check or show password field
                return;
            }

            // Make AJAX request to check if user exists
            const formElement = this.elements.emailInput?.closest('form');
            if (!formElement) {
                return;
            }

            // Find the component ID from the form
            const componentInput = formElement.querySelector('input[name="_do"]') as HTMLInputElement;
            if (!componentInput) {
                return;
            }

            // Extract component name from the _do value
            const doValue = componentInput.value;
            const componentMatch = doValue.match(/^(.*?)-orderCartForm-submit$/);
            if (!componentMatch) {
                return;
            }

            const componentName = componentMatch[1];
            const url = `?do=${componentName}-checkEmail&email=${encodeURIComponent(email)}`;

            naja.makeRequest('GET', url, null, {
                history: false,
            }).then((payload: any) => {
                if (payload.userExists && this.elements.passwordInput) {
                    // Show password field and make it required
                    this.elements.passwordInput.style.display = 'block';
                    this.elements.passwordInput.setAttribute('required', '');
                    const passwordContainer = this.elements.passwordInput.closest('.form-group') as HTMLElement;
                    if (passwordContainer) {
                        passwordContainer.classList.remove('hidden');
                        passwordContainer.style.display = 'block';
                    }

                    // Add visual indicator that password is required
                    const passwordLabel = passwordContainer?.querySelector('.base-cart-form__label');
                    if (passwordLabel && !passwordLabel.textContent?.includes('*')) {
                        passwordLabel.textContent = 'Heslo *';
                    }
                } else if (this.elements.passwordInput) {
                    // Hide password field and remove required attribute
                    this.elements.passwordInput.style.display = 'none';
                    this.elements.passwordInput.removeAttribute('required');
                    this.elements.passwordInput.value = '';
                    const passwordContainer = this.elements.passwordInput.closest('.form-group') as HTMLElement;
                    if (passwordContainer) {
                        passwordContainer.classList.add('hidden');
                        passwordContainer.style.display = 'none';
                    }

                    // Remove required indicator
                    const passwordLabel = passwordContainer?.querySelector('.base-cart-form__label');
                    if (passwordLabel) {
                        passwordLabel.textContent = 'Heslo';
                    }
                }
            });
        };

        this.elements.emailInput.addEventListener('blur', this.boundHandlers.emailBlurHandler);
    }

    private setupLoginButton(): void {
        if (!this.elements.loginButton || !this.elements.emailInput || !this.elements.passwordInput) {
            return;
        }

        this.boundHandlers.loginButtonHandler = () => {
            const email = this.elements.emailInput?.value;
            const password = this.elements.passwordInput?.value;

            if (!email || !password) {
                this.showLoginMessage('E-mail a heslo jsou povinné.', 'error');
                return;
            }

            // Disable button during request
            if (this.elements.loginButton) {
                this.elements.loginButton.disabled = true;
                this.elements.loginButton.textContent = 'Přihlašuji...';
            }

            // Get component info for AJAX call
            const formElement = this.elements.emailInput?.closest('form');
            const componentInput = formElement?.querySelector('input[name="_do"]') as HTMLInputElement;
            if (!componentInput) {
                this.showLoginMessage('Chyba při přihlašování.', 'error');
                this.resetLoginButton();
                return;
            }

            const doValue = componentInput.value;
            const componentMatch = doValue.match(/^(.*?)-orderCartForm-submit$/);
            if (!componentMatch) {
                this.showLoginMessage('Chyba při přihlašování.', 'error');
                this.resetLoginButton();
                return;
            }

            const componentName = componentMatch[1];
            const url = `?do=${componentName}-ajaxLogin&email=${encodeURIComponent(email)}&password=${encodeURIComponent(password)}`;

            naja.makeRequest('GET', url, null, {
                history: false,
            })
                .then((payload: any) => {
                    if (payload.success) {
                        this.showLoginMessage(payload.message, 'success');
                        // Hide password field since user is now logged in
                        this.hidePasswordField();
                        // Optionally reload snippets to update user interface
                        setTimeout(() => {
                            window.location.reload();
                        }, 1000);
                    } else {
                        this.showLoginMessage(payload.message, 'error');
                    }
                    this.resetLoginButton();
                })
                .catch(() => {
                    this.showLoginMessage('Chyba při přihlašování.', 'error');
                    this.resetLoginButton();
                });
        };

        this.elements.loginButton.addEventListener('click', this.boundHandlers.loginButtonHandler);
    }

    private showLoginMessage(message: string, type: 'success' | 'error'): void {
        if (this.elements.loginMessage) {
            this.elements.loginMessage.textContent = message;
            this.elements.loginMessage.style.display = 'block';
            this.elements.loginMessage.style.color = type === 'success' ? '#28a745' : '#dc3545';
        }
    }

    private resetLoginButton(): void {
        if (this.elements.loginButton) {
            this.elements.loginButton.disabled = false;
            this.elements.loginButton.textContent = 'Přihlásit se';
        }
    }

    private hidePasswordField(): void {
        if (this.elements.passwordInput) {
            this.elements.passwordInput.style.display = 'none';
            this.elements.passwordInput.removeAttribute('required');
            this.elements.passwordInput.value = '';
            const passwordContainer = this.elements.passwordInput.closest('.form-group') as HTMLElement;
            if (passwordContainer) {
                passwordContainer.classList.add('hidden');
                passwordContainer.style.display = 'none';
            }
        }
    }

    private updatePhonePrecode(prefix: string): void {
        if (!this.elements.phonePrecodeInput || !this.elements.phonePrecodeSelect) {
            return;
        }

        // Update the hidden input value
        this.elements.phonePrecodeInput.value = prefix;

        // Update the visible select display
        const selectedValueSpan = this.elements.phonePrecodeSelect.querySelector('.selected-value');
        if (selectedValueSpan) {
            selectedValueSpan.textContent = prefix;
        }

        // Update the selected option
        const options = this.elements.phonePrecodeSelect.querySelectorAll('.option');
        options.forEach((option) => {
            option.classList.remove('selected');
            if (option.getAttribute('data-value') === prefix) {
                option.classList.add('selected');
            }
        });
    }

    private switchToTextInput(): void {
        if (!this.elements.phonePrecodeSelect || !this.elements.phonePrecodeTextInput) {
            return;
        }

        // Hide the dropdown select
        this.elements.phonePrecodeSelect.style.display = 'none';

        // Show the text input
        this.elements.phonePrecodeTextInput.style.display = 'block';

        // Clear any existing value in the text input
        this.elements.phonePrecodeTextInput.value = '';

        // Focus the text input for better UX
        this.elements.phonePrecodeTextInput.focus();
    }

    private switchToDropdown(): void {
        if (!this.elements.phonePrecodeSelect || !this.elements.phonePrecodeTextInput) {
            return;
        }

        // Show the dropdown select
        this.elements.phonePrecodeSelect.style.display = 'block';

        // Hide the text input
        this.elements.phonePrecodeTextInput.style.display = 'none';

        // Clear the text input value
        this.elements.phonePrecodeTextInput.value = '';
    }

    private saveCheckboxStates(): void {
        this.checkboxStates.newsletter = this.elements.summaryNewsletter?.checked ?? false;
        this.checkboxStates.terms = this.elements.summaryTerms?.checked ?? false;
    }

    private restoreCheckboxStates(): void {
        if (this.elements.summaryNewsletter) {
            this.elements.summaryNewsletter.checked = this.checkboxStates.newsletter;
        }
        if (this.elements.formNewsletter) {
            this.elements.formNewsletter.checked = this.checkboxStates.newsletter;
        }
        if (this.elements.summaryTerms) {
            this.elements.summaryTerms.checked = this.checkboxStates.terms;
        }
        if (this.elements.formTerms) {
            this.elements.formTerms.checked = this.checkboxStates.terms;
        }

        // Also sync initial state after restoration to handle any pre-populated values
        this.syncInitialFormState();
    }

    private setupNajaSnippetHandler(): void {
        // Only set up Naja handlers once to prevent duplicates
        if (this.najaHandlersSetup) {
            return;
        }

        // Re-initialize after Naja snippet updates
        naja.snippetHandler.addEventListener('beforeUpdate', (event: { detail: { snippet: { id: string } } }) => {
            if (
                event.detail.snippet.id === 'snippet-cartSummary' ||
                event.detail.snippet.id === 'snippet-orderCartForm'
            ) {
                // Save checkbox states before snippet updates
                this.saveCheckboxStates();
            }
        });

        naja.snippetHandler.addEventListener('afterUpdate', (event: { detail: { snippet: { id: string } } }) => {
            if (
                event.detail.snippet.id === 'snippet-cartSummary' ||
                event.detail.snippet.id === 'snippet-orderCartForm-deliveryAddress'
            ) {
                // Re-get elements and re-initialize after snippet update
                this.elements = this.getElements();
                this.reinitializeHandlers();
                // Restore checkbox states after reinitialization
                this.restoreCheckboxStates();
            }

            if (event.detail.snippet.id === 'snippet-orderCartForm-deliveryAddress') {
                CustomSelect.init();
            }
        });

        this.najaHandlersSetup = true;
    }

    private reinitializeHandlers(): void {
        // Re-initialize only the handlers, not the Naja snippet handlers
        this.cleanup();
        this.syncCheckboxes();
        this.syncInitialFormState();
        this.setupSubmitHandler();
        this.setupPhonePrecodeSync();
        this.setupCustomSelectHandlers();
        this.setupEmailValidation();
        this.setupLoginButton();
        this.setupForgotPasswordLink();
        this.setupContinueWithoutLoginLink();
        this.setupSendResetButton();
    }

    private setupForgotPasswordLink(): void {
        if (!this.elements.forgotPasswordLink) {
            return;
        }

        this.boundHandlers.forgotPasswordHandler = (e: Event) => {
            e.preventDefault();
            if (this.elements.forgotPasswordContainer) {
                this.elements.forgotPasswordContainer.style.display = 'block';
            }
        };

        this.elements.forgotPasswordLink.addEventListener('click', this.boundHandlers.forgotPasswordHandler);
    }

    private setupContinueWithoutLoginLink(): void {
        if (!this.elements.continueWithoutLoginLink) {
            return;
        }

        this.boundHandlers.continueWithoutLoginHandler = (e: Event) => {
            e.preventDefault();
            // Hide the password field and allow the user to continue
            this.hidePasswordField();
            this.showLoginMessage('Pokračujete bez přihlášení.', 'success');
        };

        this.elements.continueWithoutLoginLink.addEventListener(
            'click',
            this.boundHandlers.continueWithoutLoginHandler,
        );
    }

    private setupSendResetButton(): void {
        if (!this.elements.sendResetButton || !this.elements.emailInput) {
            return;
        }

        this.boundHandlers.sendResetHandler = () => {
            const email = this.elements.emailInput?.value;

            if (!email) {
                this.showLoginMessage('E-mail je povinný.', 'error');
                return;
            }

            // Disable button during request
            if (this.elements.sendResetButton) {
                this.elements.sendResetButton.disabled = true;
                this.elements.sendResetButton.textContent = 'Odesílám...';
            }

            // Get component info for AJAX call
            const formElement = this.elements.emailInput?.closest('form');
            const componentInput = formElement?.querySelector('input[name="_do"]') as HTMLInputElement;
            if (!componentInput) {
                this.showLoginMessage('Chyba při odesílání.', 'error');
                this.resetSendResetButton();
                return;
            }

            const doValue = componentInput.value;
            const componentMatch = doValue.match(/^(.*?)-orderCartForm-submit$/);
            if (!componentMatch) {
                this.showLoginMessage('Chyba při odesílání.', 'error');
                this.resetSendResetButton();
                return;
            }

            const componentName = componentMatch[1];
            const url = `?do=${componentName}-sendPasswordReset&email=${encodeURIComponent(email)}`;

            naja.makeRequest('GET', url, null, {
                history: false,
            })
                .then((payload: any) => {
                    if (payload.success) {
                        this.showLoginMessage(payload.message, 'success');
                        // Hide the forgot password container after successful send
                        if (this.elements.forgotPasswordContainer) {
                            this.elements.forgotPasswordContainer.style.display = 'none';
                        }
                    } else {
                        this.showLoginMessage(payload.message, 'error');
                    }
                    this.resetSendResetButton();
                })
                .catch(() => {
                    this.showLoginMessage('Chyba při odesílání.', 'error');
                    this.resetSendResetButton();
                });
        };

        this.elements.sendResetButton.addEventListener('click', this.boundHandlers.sendResetHandler);
    }

    private resetSendResetButton(): void {
        if (this.elements.sendResetButton) {
            this.elements.sendResetButton.disabled = false;
            this.elements.sendResetButton.textContent = 'Odeslat odkaz pro obnovení hesla';
        }
    }

    private setupSubmissionStateHandlers(): void {
        // Reset submission flag when AJAX request completes
        naja.addEventListener('complete', () => {
            this.isSubmitting = false;
        });

        // Reset submission flag on errors
        naja.addEventListener('error', () => {
            this.isSubmitting = false;
        });
    }
}
